import { normalizeDocumentationRuntime } from '@common/components/MultiCodeSnippet/mapRuntimeToSnippetLanguage'
import type { MultiCodeVariant } from '@common/components/MultiCodeSnippet/MultiCodeSnippet'

const MULTI_OPEN = '```multi'

const INNER_OPEN_REGEX = /^```([^\n`]+)\n/
const OUTER_CLOSE_REGEX = /^```\s*(?:\n|$)/

function parseInnerVariant(content: string, start: number): { variant: MultiCodeVariant; end: number } | null {
  const slice = content.slice(start)
  const open = slice.match(INNER_OPEN_REGEX)
  if (!open?.[1]) return null

  const fenceLang = open[1].trim()
  const codeStart = start + open[0].length
  const closeMatch = content.slice(codeStart).match(/\n```(?:\n|$)/)
  if (!closeMatch || closeMatch.index === undefined) return null

  const closeIndex = codeStart + closeMatch.index
  const code = content.slice(codeStart, closeIndex).replace(/\n$/, '')
  const end = closeIndex + closeMatch[0].length

  return {
    variant: {
      runtime: normalizeDocumentationRuntime(fenceLang),
      language: fenceLang,
      code,
    },
    end,
  }
}

function isOuterMultiClose(content: string, start: number): boolean {
  return OUTER_CLOSE_REGEX.test(content.slice(start))
}

/**
 * Parse ```multi blocks with nested per-runtime fences.
 * A naive non-greedy regex stops at the first inner closing fence.
 */
export function transformMultiCodeBlocks(content: string): {
  transformed: string
  multiCodeBlocks: Map<string, MultiCodeVariant[]>
} {
  const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const multiCodeBlocks = new Map<string, MultiCodeVariant[]>()
  let counter = 0
  let output = ''
  let index = 0

  while (index < normalized.length) {
    const openIndex = normalized.indexOf(MULTI_OPEN, index)
    if (openIndex === -1) {
      output += normalized.slice(index)
      break
    }

    output += normalized.slice(index, openIndex)

    let pos = openIndex + MULTI_OPEN.length
    if (normalized[pos] === '\n') pos++

    const variants: MultiCodeVariant[] = []

    while (pos < normalized.length) {
      if (isOuterMultiClose(normalized, pos) && variants.length > 0) {
        const close = normalized.slice(pos).match(OUTER_CLOSE_REGEX)
        pos += close?.[0].length ?? 3
        break
      }

      const parsed = parseInnerVariant(normalized, pos)
      if (!parsed) break

      variants.push(parsed.variant)
      pos = parsed.end
    }

    if (variants.length > 0) {
      const contentId = `multi-code-${counter++}`
      multiCodeBlocks.set(contentId, variants)
      output += `<div class="multi-code-wrapper" data-content-id="${contentId}"></div>`
    } else {
      output += normalized.slice(openIndex, pos)
    }

    index = pos
  }

  return { transformed: output, multiCodeBlocks }
}
